import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { PublicShell } from "../components/PublicShell";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";

export default function FinancingPage() {
  const [params] = useSearchParams();
  const vehicleId = params.get("vehicle") || "";
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    full_name: "", email: "", phone: "", requested_amount: 50000,
    monthly_income: 0, housing_expense: 0, employer: "", employment_years: 1,
    bankruptcy_history: false,
  });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      // Create lightweight customer
      const { data: cust } = await api.post("/customers", {
        full_name: form.full_name, email: form.email, phone: form.phone, monthly_income: form.monthly_income, employer: form.employer,
      });
      await api.post("/finance-apps", {
        customer_id: cust.id, vehicle_id: vehicleId, requested_amount: Number(form.requested_amount),
        monthly_income: Number(form.monthly_income), housing_expense: Number(form.housing_expense),
        employer: form.employer, employment_years: Number(form.employment_years),
        bankruptcy_history: form.bankruptcy_history,
      });
      setSubmitted(true);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSubmitting(false); }
  };

  if (submitted) {
    return (
      <PublicShell>
        <section className="max-w-2xl mx-auto px-6 lg:px-8 pt-32 pb-24 text-center">
          <CheckCircle2 className="w-16 h-16 mx-auto mb-6" style={{ color: "var(--wakzy-gold)" }} />
          <h1 className="font-heading text-4xl mb-3 chrome-text inline-block">Application received.</h1>
          <p className="text-slate-300 max-w-md mx-auto">Our finance team will review your application and reach out within 1 business hour.</p>
        </section>
      </PublicShell>
    );
  }

  return (
    <PublicShell>
      <section className="finance-bg pt-32 pb-16">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <div className="brand-divider max-w-xs mb-4"><span className="dot" /><span className="label-eyebrow" style={{ color: "var(--wakzy-gold)" }}>Pre-approval</span></div>
          <h1 className="font-heading text-4xl sm:text-5xl mb-3"><span className="chrome-text">Get pre-approved</span> <span className="gold-text">in minutes.</span></h1>
          <p className="text-slate-300">Multiple lenders, one application. We never share your information.</p>
        </div>
      </section>
      <section className="max-w-3xl mx-auto px-6 lg:px-8 py-12">
        <form onSubmit={submit} className="surface p-8 rounded-md space-y-4" data-testid="financing-form">
          <div className="grid md:grid-cols-2 gap-4">
            {[
              { k: "full_name", label: "Full legal name", req: true },
              { k: "email", label: "Email", type: "email", req: true },
              { k: "phone", label: "Phone" },
              { k: "employer", label: "Employer" },
              { k: "monthly_income", label: "Monthly income ($)", type: "number" },
              { k: "housing_expense", label: "Housing expense ($)", type: "number" },
              { k: "employment_years", label: "Years at employer", type: "number" },
              { k: "requested_amount", label: "Loan amount ($)", type: "number" },
            ].map((f) => (
              <div key={f.k}>
                <Label className="text-sm text-slate-300">{f.label}{f.req && <span className="text-[#E63946]"> *</span>}</Label>
                <Input
                  type={f.type || "text"}
                  required={f.req}
                  value={form[f.k]}
                  onChange={(e) => setForm({ ...form, [f.k]: f.type === "number" ? Number(e.target.value) : e.target.value })}
                  className="bg-[#04060d] border-white/10 mt-1 h-11"
                  data-testid={`finance-${f.k}-input`}
                />
              </div>
            ))}
          </div>
          <div className="flex items-center gap-3 surface-elev p-4 rounded-sm">
            <Switch checked={form.bankruptcy_history} onCheckedChange={(v) => setForm({ ...form, bankruptcy_history: v })} />
            <span className="text-sm">Bankruptcy in last 7 years</span>
          </div>
          <Button type="submit" disabled={submitting} className="btn-cta rounded-sm h-12 px-8" data-testid="financing-submit-btn">
            {submitting ? "Submitting…" : "Submit Application"}
          </Button>
          <p className="text-xs text-slate-500">Submitting this form does not constitute a credit application or guarantee approval.</p>
        </form>
      </section>
    </PublicShell>
  );
}
